import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress
} from '@mui/material';
import { saveResearchTemplate } from '../../utils/researchTemplatesUtils';

/**
 * Dialog for saving a research configuration as a template
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Function to call when dialog is closed (receives success flag)
 * @param {Object} jobConfig - Research job configuration to save as template
 * @param {string} jobConfig.selectedColumn - The selected column name for the job
 * @param {string} jobConfig.instructions - Research instructions
 * @param {boolean} jobConfig.enableQualification - Whether qualification is enabled
 * @param {boolean} jobConfig.enableCustomFields - Whether custom fields are enabled
 * @param {Array} jobConfig.customFields - Custom field definitions
 * @param {boolean} jobConfig.enableWebSearch - Whether web search is enabled
 * @param {string} jobConfig.webSearchInstructions - Web search instructions
 */
const SaveResearchTemplateDialog = ({ open, onClose, jobConfig }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Build the template data from job configuration
      const templateData = {
        name: name.trim(),
        description: description.trim(),
        selectedColumn: jobConfig?.selectedColumn || '',
        instructions: jobConfig?.instructions || '',
        enableQualification: jobConfig?.enableQualification !== false,
        enableCustomFields: jobConfig?.enableCustomFields || false,
        customFields: jobConfig?.customFields || [],
        enableWebSearch: jobConfig?.enableWebSearch || false,
        webSearchInstructions: jobConfig?.webSearchInstructions || '',
        isFavorite
      };

      console.log('[SAVE-RESEARCH-TEMPLATE] Saving template:', templateData);

      await saveResearchTemplate(templateData);

      setLoading(false);
      onClose(true); // Close with success flag
    } catch (error) {
      console.error('[SAVE-RESEARCH-TEMPLATE] Error saving template:', error);
      setError(error.message || 'An error occurred while saving the template');
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setName('');
    setDescription('');
    setIsFavorite(false);
    setError('');
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Research Template</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Template Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., B2B SaaS Qualification"
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Description (optional)"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe what this template is for..."
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
            />
          }
          label="Save as favorite"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveResearchTemplateDialog;
